// utils/aliyunCaptcha.js

/**
 * 阿里云验证码配置
 * @param {Object} options 配置选项
 * @param {String} options.element 渲染验证码的元素选择器
 * @param {String} options.button 触发验证码的按钮选择器
 * @param {String} options.mode 验证码模式，popup表示弹出式，embed表示嵌入式。无需修改
 * @param {Function} options.onSuccess 验证成功回调
 * @param {Function} options.onFail 验证失败回调
 * @param {Function} options.onGetInstance 获取实例回调
 * @param {Function} options.onClose 弹窗关闭回调
 * @param {Function} options.onError 错误回调
 */
const ALIYUN_CAPTCHA_CONFIG = {
  region: 'cn',
  prefix: '1d1n4d',
  scriptUrl: 'https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js',
  sceneId: '1r0wupk8',
  loadTimeout: 10000 // 加载超时时间
}

/**
 * 加载阿里云验证码脚本
 * @returns {Promise<Function>} 返回 initAliyunCaptcha 函数
 */
export function loadAliyunCaptchaScript() {
  return new Promise((resolve, reject) => {
    // 如果验证码 JS 已存在，直接返回
    if (window.initAliyunCaptcha) {
      return resolve(window.initAliyunCaptcha)
    }

    // 检查是否正在加载中
    if (window.__aliyunCaptchaLoading) {
      return waitForCaptchaLoad(resolve, reject)
    }

    // 标记正在加载
    window.__aliyunCaptchaLoading = true

    // 设置全局配置
    window.AliyunCaptchaConfig = {
      region: ALIYUN_CAPTCHA_CONFIG.region,
      prefix: ALIYUN_CAPTCHA_CONFIG.prefix
    }

    const script = document.createElement('script')
    script.src = ALIYUN_CAPTCHA_CONFIG.scriptUrl
    script.async = true

    script.onload = () => {
      window.__aliyunCaptchaLoading = false
      if (window.initAliyunCaptcha) {
        resolve(window.initAliyunCaptcha)
      } else {
        reject(new Error('阿里云验证码脚本加载失败：initAliyunCaptcha 未定义'))
      }
    }

    script.onerror = () => {
      window.__aliyunCaptchaLoading = false
      document.head.removeChild(script)
      reject(new Error('阿里云验证码脚本加载失败：网络错误'))
    }

    document.head.appendChild(script)
  })
}

/**
 * 等待验证码加载完成
 * @private
 */
function waitForCaptchaLoad(resolve, reject) {
  let elapsed = 0
  const interval = 100

  const checkInterval = setInterval(() => {
    elapsed += interval

    if (window.initAliyunCaptcha) {
      clearInterval(checkInterval)
      resolve(window.initAliyunCaptcha)
    } else if (elapsed >= ALIYUN_CAPTCHA_CONFIG.loadTimeout) {
      clearInterval(checkInterval)
      reject(new Error('阿里云验证码脚本加载超时'))
    }
  }, interval)
}

/**
 * 初始化阿里云验证码
 * @param {Object} options - 配置选项
 * @returns {Promise<Object>} 返回验证码实例
 */
export async function initAliyunCaptcha(options = {}) {
  try {
    // 加载脚本
    const initFn = await loadAliyunCaptchaScript()

    // 解构配置选项，设置默认值
    const {
      mode = 'popup',
      element,
      button,
      sceneId = ALIYUN_CAPTCHA_CONFIG.sceneId,
      onSuccess,
      onFail,
      onGetInstance,
      onClose,
      onError,
      slideStyle = { width: 360, height: 40 },
      language = 'cn'
    } = options

    // 验证必要参数
    if (mode === 'embed' && !element) {
      throw new Error('嵌入式模式必须提供 element 参数')
    }

    return new Promise((resolve, reject) => {
      const config = {
        SceneId: sceneId,
        mode,
        element,
        button,
        slideStyle,
        language,

        success: captchaVerifyParam => {
          safeCallback(onSuccess, captchaVerifyParam, '验证成功回调')
        },

        fail: result => {
          console.warn('验证码验证失败:', result)
          safeCallback(onFail, result, '验证失败回调')
        },

        getInstance: instance => {
          safeCallback(onGetInstance, instance, '获取实例回调')
          resolve(instance)
        },

        onClose: () => {
          safeCallback(onClose, undefined, '关闭回调')
        },

        onError: error => {
          console.error('验证码初始化错误:', error)
          safeCallback(onError, error, '错误回调')
          reject(error)
        }
      }

      initFn(config)
    })
  } catch (error) {
    console.error('初始化阿里云验证码失败:', error)
    throw error
  }
}

/**
 * 安全执行回调函数
 * @private
 */
function safeCallback(callback, data, callbackName) {
  if (typeof callback === 'function') {
    try {
      callback(data)
    } catch (error) {
      console.error(`${callbackName}执行出错:`, error)
    }
  }
}
